import { useParams, Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowLeft, Linkedin, CalendarDays } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BlogCard from "@/components/blog/BlogCard";
import { supabase } from "@/integrations/supabase/client";
import { CONTENT_TYPES, type ContentTypeKey } from "@/lib/resourceTypes";
import { authorSlug } from "@/lib/authorSlug";
import { uiT } from "@/i18n/ui";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";

const SITE_URL = "https://qbdgroup.com";
const SITE_NAME = "QbD Group";

const Author = () => {
  const { slug } = useParams<{ slug: string }>();
  const lang = useLanguage();
  const t = uiT(lang);

  // Fetch author profile from the authors table (with prefix fallback for legacy composite slugs)
  const { data: authorProfile, isLoading: profileLoading } = useQuery({
    queryKey: ["author-profile", slug],
    queryFn: async () => {
      // Try exact match first
      const { data, error } = await supabase
        .from("authors")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (data) return data;

      // Fallback: legacy composite slugs like "pieter-bogaert-senior-consultant-ivd-regulatory-affairs"
      // Find an author whose slug is a prefix of the URL slug
      const { data: allAuthors, error: allErr } = await supabase
        .from("authors")
        .select("*");
      if (allErr) throw allErr;
      const match = (allAuthors ?? []).find(
        (a) => slug!.startsWith(a.slug) && slug!.charAt(a.slug.length) === "-"
      );
      return match ?? null;
    },
    enabled: !!slug,
  });

  // Content tables that have an author field (excluding blog_posts which uses lang)
  const OTHER_CONTENT_TABLES: ContentTypeKey[] = [
    "regulatory_updates", "webinars", "white_papers", "checklists", "case_studies", "company_news", "events",
  ];

  // Fetch blog posts by this author (EN only)
  const { data: posts = [], isLoading: postsLoading } = useQuery({
    queryKey: ["author-posts", slug, authorProfile?.name],
    queryFn: async () => {
      const { data: allPosts, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("lang", "en")
        .not("published_at", "is", null)
        .order("published_at", { ascending: false });
      if (error) throw error;
      const authorName = authorProfile?.name?.toLowerCase();
      return (allPosts ?? []).filter((p) => {
        if (!p.author) return false;
        if (authorSlug(p.author) === slug) return true;
        if (authorName && p.author.toLowerCase().includes(authorName)) return true;
        return false;
      });
    },
    enabled: !!slug && !profileLoading,
  });

  // Fetch other content types by this author (use English title/slug only)
  const { data: otherContent = [], isLoading: otherLoading } = useQuery({
    queryKey: ["author-other-content", slug, authorProfile?.name],
    queryFn: async () => {
      const authorName = authorProfile?.name?.toLowerCase();
      if (!authorName) return [];

      const promises = OTHER_CONTENT_TABLES.map(async (key) => {
        let builder = (supabase as any)
          .from(key)
          .select("id, title, slug, summary, featured_image, category, published_at, author, tags, content")
          .not("published_at", "is", null)
          .order("published_at", { ascending: false });

        // Webinars: only parent entries, EN lang
        if (key === "webinars") {
          builder = builder.is("parent_slug", null).eq("lang", "en");
        }

        const { data, error } = await builder;
        if (error || !data) return [];

        return (data as any[])
          .filter((row) => {
            if (!row.author) return false;
            if (authorSlug(row.author) === slug) return true;
            if (row.author.toLowerCase().includes(authorName)) return true;
            return false;
          })
          .map((row) => ({
            ...row,
            contentType: key,
            linkPrefix: CONTENT_TYPES.find((t) => t.key === key)?.route ?? "/en/blog",
          }));
      });

      return (await Promise.all(promises)).flat();
    },
    enabled: !!slug && !profileLoading && !!authorProfile?.name,
  });

  const allContent = [
    ...posts.map((p) => ({ ...p, contentType: "blog_posts" as ContentTypeKey, linkPrefix: "/en/blog" })),
    ...otherContent,
  ].sort((a, b) => {
    if (!a.published_at && !b.published_at) return 0;
    if (!a.published_at) return 1;
    if (!b.published_at) return -1;
    return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
  });

  const isLoading = profileLoading || postsLoading || otherLoading;
  const name = authorProfile?.name ?? slug ?? "";
  const role = authorProfile?.role ?? null;
  // Always point canonical to the primary EN version for SEO consolidation
  const canonicalUrl = `${SITE_URL}/en/author/${slug}`;

  // ProfilePage + Person JSON-LD
  const profilePageJsonLd = authorProfile
    ? {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        name: `${authorProfile.name} — ${SITE_NAME}`,
        url: canonicalUrl,
        mainEntity: {
          "@type": "Person",
          name: authorProfile.name,
          ...(role ? { jobTitle: role } : {}),
          ...(authorProfile.credentials ? { honorificSuffix: authorProfile.credentials } : {}),
          url: canonicalUrl,
          ...(authorProfile.linkedin_url ? { sameAs: [authorProfile.linkedin_url] } : {}),
          ...(authorProfile.avatar_url ? { image: authorProfile.avatar_url } : {}),
          ...(authorProfile.bio ? { description: authorProfile.bio } : {}),
          worksFor: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        },
      }
    : null;

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />

      {authorProfile && (
        <SEOHead
          title={`${name} | ${SITE_NAME}`}
          description={authorProfile.bio || `Articles by ${name} at ${SITE_NAME}.`}
          canonical={canonicalUrl}
          ogImage={authorProfile.avatar_url || undefined}
        >
          {profilePageJsonLd && (
            <script type="application/ld+json">{JSON.stringify(profilePageJsonLd)}</script>
          )}
        </SEOHead>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-40">
          <h1 className="sr-only">{slug?.replace(/-/g, " ") ?? "Author"}</h1>
          <Loader2 className="animate-spin text-muted-foreground" size={32} />
        </div>
      ) : !authorProfile ? (
        <div className="pt-36 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground">{t("authorNotFound")}</h1>
          <Link
            to="/en/blog"
            className="mt-4 inline-flex items-center gap-1 text-qbd-blue font-semibold"
          >
            <ArrowLeft size={16} /> {t("backToBlog")}
          </Link>
        </div>
      ) : (
        <>
          {/* Author hero */}
          <section className="bg-primary pt-40 pb-16">
            <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Breadcrumb */}
                <nav aria-label="Breadcrumb" className="mb-6">
                  <ol className="flex items-center gap-2 text-sm text-primary-foreground/50">
                    <li><Link to={localizeHref("/", lang)} className="hover:text-primary-foreground transition-colors">{t("home")}</Link></li>
                    <li aria-hidden="true">/</li>
                    <li><span className="text-primary-foreground/60">Authors</span></li>
                    <li aria-hidden="true">/</li>
                    <li className="text-primary-foreground/70">{name}</li>
                  </ol>
                </nav>

                <div className="flex items-start gap-5">
                  {/* Avatar */}
                  {authorProfile.avatar_url ? (
                    <img
                      src={authorProfile.avatar_url}
                      alt={name}
                      className="w-20 h-20 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary-foreground/20 flex items-center justify-center text-primary-foreground text-2xl font-bold shrink-0">
                      {name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight">
                      {name}
                    </h1>
                    {(authorProfile.credentials || role) && (
                      <p className="mt-1 text-primary-foreground/60 text-sm">
                        {[authorProfile.credentials, role].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {authorProfile.bio && (
                      <p className="mt-3 text-primary-foreground/70 text-sm leading-relaxed max-w-xl">
                        {authorProfile.bio}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <p className="text-primary-foreground/50 text-sm">
                        {allContent.length} article{allContent.length !== 1 ? "s" : ""}
                      </p>
                      {authorProfile.linkedin_url && (
                        <a
                          href={authorProfile.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-foreground/50 hover:text-primary-foreground transition-colors"
                          aria-label={`${name} on LinkedIn`}
                        >
                          <Linkedin size={18} />
                        </a>
                      )}
                      {(authorProfile as any).meeting_url && (
                        <a
                          href={(authorProfile as any).meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-accent text-white font-semibold text-sm hover:bg-accent/90 transition-colors"
                        >
                          <CalendarDays size={15} />
                          Book a meeting
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Posts grid */}
          <section className="py-20 sm:py-28 bg-background">
            <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allContent.map((item, i) => (
                  <BlogCard key={`${item.contentType}-${item.id}`} post={item} index={i} linkPrefix={item.linkPrefix} />
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default Author;
